import { Injectable, computed, inject } from '@angular/core'
import { rxResource } from '@angular/core/rxjs-interop'

import { catchError, of } from 'rxjs'

import { UserPreferencesApiService } from '@flowx/designer/shared/api/user-preferences.api'

/**
 * The workspace the user last picked, kept per user by designer-user, so landing takes them back
 * to it on any browser and after logging out.
 *
 * A pick saves it: the switcher, a row on the org Workspaces page and the logo. Landing saves the
 * workspace it chose too, which replaces a saved one that is gone. A link on its own and
 * Organization Settings never do.
 */
@Injectable({ providedIn: 'root' })
export class LastUsedWorkspaceService {
  #userPreferencesApi = inject(UserPreferencesApiService)

  /**
   * Read once per page load, as soon as anything injects this service: the header on every
   * authenticated page, or the landing guard first. That costs a request even on pages that never
   * use it, but its params never change, so nothing can start a second read that would overwrite
   * a pick saved in the meantime. In this tab it only changes through `save()`; a pick on another
   * browser shows on the next page load.
   *
   * A failed read stays in the resource: `workspaceId` is `null`, so landing falls back to the
   * default workspace, and `saveLandingPick()` can tell it apart from a user who has none.
   */
  #lastUsedWorkspace = rxResource({
    stream: () => this.#userPreferencesApi.getLastUsedWorkspaceId(),
  })

  /** `null` when the user has none or it couldn't be read, and until it is read. */
  workspaceId = computed(() =>
    this.#lastUsedWorkspace.hasValue() ? this.#lastUsedWorkspace.value() : null
  )

  /**
   * While the read is in flight, from the moment the service is created. Once it is done,
   * `workspaceId` changes only through `save()`. A read that failed is done too, so nothing
   * waiting on it is left hanging.
   */
  loading = this.#lastUsedWorkspace.isLoading

  /**
   * Called for a pick, once its navigation succeeded. Nothing is sent when it is already the saved
   * workspace. The server stores the workspace asynchronously, after answering, so a read straight
   * after could still return the previous one. The pick is set locally instead, which also discards
   * a read still in flight. A failed save is ignored: the pick still holds until the page reloads.
   */
  save(workspaceId: string): void {
    if (workspaceId === this.workspaceId()) {
      return
    }

    this.#lastUsedWorkspace.set(workspaceId)

    this.#userPreferencesApi
      .setLastUsedWorkspaceId(workspaceId)
      .pipe(catchError(() => of(null)))
      .subscribe()
  }

  /**
   * Saves the workspace landing chose. It differs from the saved one only when that one is gone
   * (deleted, or the user's access was removed) or was never set, so the user lands there again
   * next time. Skipped when the saved one couldn't be read: landing chose the default only because
   * of that, and saving it could overwrite a workspace that is still valid.
   */
  saveLandingPick(workspaceId: string): void {
    if (this.#lastUsedWorkspace.status() !== 'error') {
      this.save(workspaceId)
    }
  }
}
